import { useState, useEffect } from 'react'
import { Outlet, useParams, useOutletContext } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { SpaceHeader } from '../features/spaces/components/SpaceHeader'
import { NewIssueModal } from '../features/spaces/components/NewIssueModal'
import { SpaceMembersModal } from '../features/spaces/components/SpaceMembersModal'
import { getSpace, type SpaceDetail } from '../features/spaces/api'
import { Plus } from 'lucide-react'
import { Button } from '../shared/components/ui/Button'
import type { AppLayoutOutletContext } from './AppLayoutOutletContext'

export default function SpaceLayout() {
  const { id } = useParams<{ id: string }>()
  const { openMobileNav } = useOutletContext<AppLayoutOutletContext>()
  const queryClient = useQueryClient()
  const { data: space, isError, refetch, isFetching } = useQuery({
    queryKey: ['spaces', 'detail', id],
    queryFn: () => getSpace(id!),
    enabled: !!id,
    staleTime: 1000 * 60 * 2,
  })
  const [membersOpen, setMembersOpen] = useState(false)
  const [newIssueOpen, setNewIssueOpen] = useState(false)

  // Member count comes straight from the loaded space (backend is the source of truth).
  const [assignedCount, setAssignedCount] = useState<number>(0)
  useEffect(() => {
    if (space) setAssignedCount((space.members ?? []).length)
  }, [space])

  // Listen to spaceMembersUpdated event across tabs/components
  useEffect(() => {
    const handler = (e: any) => {
      if (e.detail?.spaceId === id) {
        if (typeof e.detail.count === 'number') setAssignedCount(e.detail.count)
        // Reload the space so its member list (used by the assignee pickers and
        // the members modal) reflects what was just saved.
        queryClient.invalidateQueries({ queryKey: ['spaces', 'detail', id] })
      }
    }
    window.addEventListener('spaceMembersUpdated', handler)
    return () => window.removeEventListener('spaceMembersUpdated', handler)
  }, [id, queryClient])

  if (isError && !space) {
    return (
      <div className="flex flex-col h-full w-full items-center justify-center gap-3 bg-white dark:bg-slate-950 p-6 text-center">
        <p className="text-sm font-semibold text-foreground">Couldn't load this space</p>
        <p className="text-xs text-muted-foreground">Check your connection or sign in again, then retry.</p>
        <Button onClick={() => refetch()} disabled={isFetching} className="h-8 px-3 text-xs">
          {isFetching ? 'Retrying…' : 'Retry'}
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full w-full bg-white dark:bg-slate-950 font-sans">
      <SpaceHeader
        space={space}
        assignedCount={assignedCount}
        onOpenMembers={() => setMembersOpen(true)}
        onOpenMobileNav={openMobileNav}
        actions={
          <Button
            onClick={() => setNewIssueOpen(true)}
            className="flex items-center gap-1.5 h-8 px-3 text-xs font-semibold bg-blue-600 text-white rounded-md hover:bg-blue-700 shadow-xs cursor-pointer"
          >
            <Plus className="h-3.5 w-3.5" />
            <span>New Issue</span>
          </Button>
        }
      />
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        <Outlet />
      </div>

      {id && (
        <NewIssueModal
          open={newIssueOpen}
          onClose={() => setNewIssueOpen(false)}
          spaceId={id}
        />
      )}

      {/* Interactive Space Members & Role Assignment Modal */}
      {space && (
        <SpaceMembersModal
          open={membersOpen}
          onClose={() => setMembersOpen(false)}
          space={space}
          onMembersChange={(assigned) => setAssignedCount(assigned.length)}
        />
      )}
    </div>
  )
}
